import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import firebaseConfig from "../fb.json";
import logo from "../assets/logo.svg";
import "../styles/login.css";
import { logGlobalActivity } from "./cms/activityLog";

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

type InviteState = "loading" | "ready" | "signing_in" | "success" | "error";

const InviteAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<InviteState>("loading");
  const [invite, setInvite] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid invite link.");
      setState("error");
      return;
    }

    const loadInvite = async () => {
      const inviteDoc = await getDoc(doc(db, "invites", token));
      if (!inviteDoc.exists()) {
        setError("Invite not found or expired.");
        setState("error");
        return;
      }

      const data = inviteDoc.data();
      if (data.used) {
        setError("This invite has already been used.");
        setState("error");
        return;
      }

      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        setError("This invite has expired.");
        setState("error");
        return;
      }

      setInvite(data);
      setState("ready");
    };

    loadInvite().catch((err) => {
      console.error("[Invite] Error loading invite:", err);
      setError("Failed to load invite.");
      setState("error");
    });
  }, [token]);

  const acceptInvite = async () => {
    setState("signing_in");

    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user = result.user;

      // Verify email matches invite
      if (invite.email && user.email?.toLowerCase() !== invite.email.toLowerCase()) {
        await auth.signOut();
        setError(`Email mismatch. This invite is for ${invite.email}, but you signed in as ${user.email}.`);
        setState("error");
        return;
      }

      // Create user record
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        displayName: user.displayName || "",
        photoURL: user.photoURL || null,
        role: invite.role,
        projects: invite.projects || ["*"],
        invitedBy: invite.invitedBy || "",
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      });

      // Mark invite as used
      await updateDoc(doc(db, "invites", token!), { used: true });

      // Log activity
      logGlobalActivity({ action: "invite_accepted", uid: user.uid, detail: user.email || "" });

      setState("success");
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      console.error("[Invite] Error accepting:", err);
      if (err.code === "auth/popup-closed-by-user") {
        setState("ready");
        return;
      }
      setError(err.message || "Failed to accept invite.");
      setState("error");
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__logo">
          <img src={logo} alt="AMA" width="36" height="36" style={{ borderRadius: 8 }} />
          <span className="login-card__logo-text">AMA Portal</span>
        </div>

        {state === "loading" && (
          <p className="login-card__heading">Loading invite...</p>
        )}

        {state === "ready" && invite && (
          <>
            <p className="login-card__heading">
              You've been invited as <strong>{invite.role}</strong>
            </p>
            {invite.email && (
              <p style={{ textAlign: "center", fontSize: "0.8125rem", color: "var(--color-text-muted)", marginBottom: "var(--space-md)" }}>
                for {invite.email}
              </p>
            )}
            <button className="login-google" onClick={acceptInvite}>
              <svg className="login-google__icon" viewBox="0 0 24 24" width="20" height="20">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span>Accept & Sign in with Google</span>
            </button>
          </>
        )}

        {state === "signing_in" && (
          <p className="login-card__heading">Signing in...</p>
        )}

        {state === "success" && (
          <p className="login-card__heading" style={{ color: "var(--color-accent)" }}>
            Welcome! Redirecting...
          </p>
        )}

        {state === "error" && (
          <div className="login-error">{error}</div>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
